import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import {
  LIST_ACTIONS,
  LIST_GROUP_ACTIONS,
  LIST_ITEM_ACTIONS,
  ListState,
} from '@usf/ngrx-list';
import {
  ListMessage,
  ListMessageStrings,
  ListMessageType,
} from '@usf/list-types';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { HomeService } from '../../../home/services/home.service';
import { SelectedCustomerState } from '@panamax/app-state';
import { MessageTypeEnum } from 'src/app/ngrx-message/constants/messageTypeEnum';
import { Message } from 'src/app/ngrx-message/models/client/message';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../toast/toast.service';
import { PlatformService, PlatformEnum } from '@panamax/app-state';
import { ToastMessages } from '../../constants/toast-messages.enum';
import { LoadingSpinnerComponent } from '../../components/popovers/loading-spinner/loading-spinner.component';
import { ListsService } from '../../../lists/services/lists.service';
@Injectable({
  providedIn: 'root',
})
export class MessageHandlerService {
  loadingSpinnerModal: HTMLIonModalElement;
  list: ListState;
  selectedCustomer: SelectedCustomerState;
  interval: any;

  constructor(
    private modalController: ModalController,
    protected store: Store<State>,
    private homeService: HomeService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private platformService: PlatformService,
    private listsService: ListsService,
  ) {
    this.homeService.loadSelectedCustomerState$().subscribe(cust => {
      this.selectedCustomer = cust;
    });
  }

  handleNewAction(newMessage: ListMessage) {
    if (newMessage.message === ListMessageStrings.addItemsSuccess) {
      const display = this.buildDisplayMessageForAddProducts(false, newMessage);
      const message = this.createMessageForToast(false, newMessage, display);
      this.toastService.presentToast(message);
    } else if (newMessage.message === ListMessageStrings.deleteItemsSuccess) {
      const message = this.createMessageForDeleteProductsToast(
        false,
        newMessage,
      );
      this.toastService.presentToast(message);
    } else if (
      newMessage.message === ListMessageStrings.replaceProductsSuccess
    ) {
      const message = this.createMessageForToast(
        false,
        newMessage,
        ToastMessages.replaceProductSuccess,
      );
      this.toastService.presentToast(message);
    }
    if (newMessage.message === ListMessageStrings.createListInProgress) {
      this.createSpinnerModal();
      this.startCreateListTimer();
    } else if (newMessage.messageType === ListMessageType.loading) {
      this.createSpinnerModal();
      this.startTimer(newMessage);
    }
    if (newMessage.messageType === ListMessageType.success) {
      this.endTrace();
      this.dismissSpinnerModal();
      clearInterval(this.interval);
      this.writeToOfflineStorage();
    } else if (newMessage.messageType === ListMessageType.error) {
      this.endTrace();
      this.dismissSpinnerModal();
      clearInterval(this.interval);
      const message = this.createMessageForToast(
        true,
        newMessage,
        newMessage.message,
      );
      this.toastService.presentToast(message);
    }
  }

  handleCreateNewListSuccess(listTypeId: string, listId: number) {
    clearInterval(this.interval);
    this.dismissSpinnerModal();
    this.listsService.goToShoppingList([
      'lists',
      'management',
      listTypeId + '-' + listId,
    ]);
  }

  private writeToOfflineStorage() {
    this.store.dispatch(LIST_ACTIONS.writeToOfflineStorage());
    this.store.dispatch(LIST_GROUP_ACTIONS.writeToOfflineStorage());
    this.store.dispatch(LIST_ITEM_ACTIONS.writeToOfflineStorage());
  }

  private endTrace() {
    this.store.dispatch(
      LIST_ACTIONS.endListAction({
        tracking: {
          tracing: {
            data: {
              traceContext: 'listMaintenance',
              isEndOfTrace: true,
              isStartOfTrace: false,
            },
          },
        },
      }),
    );
  }

  startCreateListTimer() {
    clearInterval(this.interval);
    this.interval = setInterval(() => {
      clearInterval(this.interval);
      this.dismissSpinnerModal();
      this.showGenericErrorMessage();
    }, 30000);
  }

  startTimer(message: ListMessage) {
    clearInterval(this.interval);
    this.interval = setInterval(() => {
      this.handleTimeout(message);
    }, 30000);
  }

  startVendorAndProductTimer() {
    clearInterval(this.interval);
    this.interval = setInterval(() => {
      const message = this.createGenericErrorMessage();
      this.toastService.presentToast(message);
      this.dismissSpinnerModal();
      clearInterval(this.interval);
    }, 30000);
  }

  showGenericErrorMessage() {
    const message = this.createGenericErrorMessage();
    this.toastService.presentToast(message);
  }

  handleTimeout(listMessage: ListMessage) {
    if (listMessage.message === ListMessageStrings.addItemsInProgress) {
      const display = this.buildDisplayMessageForAddProducts(true, listMessage);
      const message = this.createMessageForToast(true, listMessage, display);
      this.toastService.presentToast(message);
    } else if (
      listMessage.message === ListMessageStrings.deleteItemsInProgress
    ) {
      const message = this.createMessageForDeleteProductsToast(
        true,
        listMessage,
      );
      this.toastService.presentToast(message);
    } else if (listMessage.message === ListMessageStrings.addGroupInProgress) {
      this.showGenericErrorMessage();
    }
    this.dismissSpinnerModal();
    this.store.dispatch(LIST_ACTIONS.initializeStorage());
    clearInterval(this.interval);
  }

  async createSpinnerModal(cancelButton?: boolean, redirectPath?: string) {
    this.loadingSpinnerModal = await this.modalController.create({
      component: LoadingSpinnerComponent,
      backdropDismiss: false,
      cssClass: 'modal-loading-spinner',
      componentProps: {
        cancelButton: !!cancelButton,
        redirectPath: redirectPath,
      },
    });
    return this.loadingSpinnerModal.present();
  }

  async dismissSpinnerModal() {
    return this.loadingSpinnerModal?.dismiss();
  }

  private createMessageForToast(
    isFailure: boolean,
    listMessage: ListMessage,
    display: string,
  ): Message {
    const message: Message = {
      type: isFailure ? MessageTypeEnum.error : MessageTypeEnum.success,
      display: display,
      sticky: true,
      read: false,
      watermark: new Date().toUTCString(),
      id:
        listMessage?.message === ListMessageStrings.addItemsSuccess
          ? 'add-products-success'
          : '',
      stack: '',
      listMessage: listMessage,
    };
    return message;
  }

  private buildDisplayMessageForAddProducts(
    isFailure: boolean,
    listMessage: ListMessage,
  ): string {
    const isMobile =
      this.platformService.platformType === PlatformEnum.mobile ? true : false;
    let display = '';
    if (!isFailure) {
      const productQuantity = listMessage.additionalInfo.productsQuantity;
      const listName = listMessage.additionalInfo.listName.toString();
      if (isMobile) {
        display = this.translateService.instant(
          'i18n.lists.addProductsSuccessTouch',
        );
      } else {
        display =
          productQuantity === 1
            ? this.translateService.instant('i18n.lists.addProductSuccess', {
                listName: listName,
              })
            : this.translateService.instant('i18n.lists.addProductsSuccess', {
                productQuantity: productQuantity,
                listName: listName,
              });
      }
    } else {
      display = this.translateService.instant('i18n.lists.addProductsFailure');
    }
    return display;
  }

  createMessageForDeleteProductsToast(
    isFailure: boolean,
    listMessage: ListMessage,
  ) {
    const display = isFailure
      ? this.translateService.instant('i18n.lists.deleteProductsFailure')
      : this.translateService.instant('i18n.lists.deleteProductsSuccess');
    const message: Message = {
      type: isFailure ? MessageTypeEnum.error : MessageTypeEnum.success,
      display: display,
      sticky: true,
      read: false,
      watermark: new Date().toUTCString(),
      id: '',
      stack: '',
      listMessage: listMessage,
    };
    return message;
  }

  createGenericErrorMessage() {
    const message = {
      type: MessageTypeEnum.error,
      display: this.translateService.instant('i18n.lists.genericError'),
      sticky: false,
      read: false,
      watermark: new Date().toUTCString(),
      id: '',
      stack: '',
    };
    return message;
  }
}
